import { apiGateway, GW } from '../config/apiGateway';

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const DEBOUNCE_MS = 650;

const isProd = process.env.NODE_ENV === 'production';
const logEnquiry = (...args) => {
  if (!isProd) console.warn('[saveEnquiry]', ...args);
};

/** Resolve LMS step label from current form progress */
export const resolveEnquiryStep = ({
  mobileVerified,
  panVerified,
  panNumber,
  otpVerified,
  aadhaarNumber,
  email,
  loanAmount,
  cityId,
  cityName,
}) => {
  if (otpVerified || (email && loanAmount && (cityId || cityName))) {
    if (email || loanAmount || cityId || cityName) return 'loan_details';
  }
  if (otpVerified) return 'aadhaar_done';
  if (panVerified || (panNumber && String(panNumber).length === 10)) {
    if (aadhaarNumber && String(aadhaarNumber).replace(/\D/g, '').length === 12) {
      return 'aadhaar';
    }
    return 'pan_done';
  }
  if (mobileVerified) return 'mobile_verified';
  return 'mobile';
};

/**
 * Cumulative enquiry payload for LMS Enquires.
 * Sends both common aliases so backend can pick either naming.
 */
export const buildEnquiryPayload = ({
  mobile = '',
  mobileVerified = false,
  firstName = '',
  middleName = '',
  lastName = '',
  pan = '',
  aadhaar = '',
  email = '',
  loanAmount = '',
  cityId = '',
  cityName = '',
  step = 'mobile',
  source = 'MrRupya',
}) => {
  const cleanMobile = String(mobile || '').replace(/\D/g, '').slice(0, 10);
  const cleanPan = String(pan || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 10);
  const cleanAadhaar = String(aadhaar || '').replace(/\D/g, '').slice(0, 12);
  const name =
    [firstName, middleName, lastName]
      .map((p) => String(p || '').trim())
      .filter(Boolean)
      .join(' ')
      .trim() || String(firstName || '').trim();

  return {
    Mobile: cleanMobile,
    mobile: cleanMobile,
    Name: name,
    first_name: String(firstName || '').trim(),
    middle_name: String(middleName || '').trim(),
    sur_name: String(lastName || '').trim(),
    mobile_verified: mobileVerified ? 1 : 0,
    pan: cleanPan,
    pancard: cleanPan,
    aadhaar: cleanAadhaar,
    aadhaar_number: cleanAadhaar,
    email: String(email || '').trim(),
    email_personal: String(email || '').trim(),
    loan_amount: String(loanAmount || '').replace(/\D/g, ''),
    city_id: cityId ? String(cityId) : '',
    city_name: String(cityName || '').trim(),
    step: String(step || 'mobile'),
    source: String(source || 'MrRupya'),
  };
};

/**
 * Fire-and-forget saveEnquiry. Never throws to callers.
 * @param {object} payload
 * @param {{ keepalive?: boolean }} [options]
 */
export const saveEnquiry = async (payload, options = {}) => {
  const mobile = String(payload?.mobile || payload?.Mobile || '').replace(/\D/g, '');
  if (!MOBILE_REGEX.test(mobile)) return;

  try {
    const resp = await apiGateway(GW.SAVE_ENQUIRY, payload, {
      keepalive: Boolean(options.keepalive),
      timeout: 20000,
    });

    if (resp?.status && resp.status >= 400) {
      logEnquiry(`HTTP ${resp.status}`);
    }
  } catch (err) {
    logEnquiry(err?.message || err);
  }
};

/**
 * Debounced autosave controller. Disable during final LEAD submit.
 */
export const createEnquiryAutosave = () => {
  let timer = null;
  let enabled = true;
  let latestPayload = null;

  const clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    setLatest(payload) {
      latestPayload = payload;
    },

    schedule(payloadOrFn) {
      if (!enabled) return;
      clear();
      timer = setTimeout(() => {
        timer = null;
        if (!enabled) return;
        const payload =
          typeof payloadOrFn === 'function' ? payloadOrFn() : payloadOrFn;
        if (!payload) return;
        latestPayload = payload;
        saveEnquiry(payload);
      }, DEBOUNCE_MS);
    },

    /** Immediate save (milestones: OTP, PAN, Aadhaar). Still silent. */
    flush(payload) {
      if (!enabled) return;
      clear();
      if (payload) latestPayload = payload;
      if (!latestPayload) return;
      return saveEnquiry(latestPayload);
    },

    flushKeepalive() {
      if (!enabled) return;
      clear();
      if (!latestPayload) return;
      return saveEnquiry(latestPayload, { keepalive: true });
    },

    disable() {
      enabled = false;
      clear();
    },

    enable() {
      enabled = true;
    },

    isEnabled() {
      return enabled;
    },
  };
};
